const reduceMotionMedia = window.matchMedia("(prefers-reduced-motion: reduce)");
const storyMotionMedia = window.matchMedia(
  "(min-width: 921px) and (min-height: 621px) and (prefers-reduced-motion: no-preference)",
);

const homeRootsWithin = (scope = document) => {
  const roots = [];
  if (scope instanceof Element && scope.matches(".ph-home")) roots.push(scope);
  scope.querySelectorAll?.(".ph-home").forEach((root) => roots.push(root));
  return roots;
};

const addMediaChangeListener = (mediaQuery, listener) => {
  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", listener);
  } else if (typeof mediaQuery.addListener === "function") {
    mediaQuery.addListener(listener);
  }
};

const revealControllers = new Map();

const destroyReveals = (homeRoot) => {
  const cleanup = revealControllers.get(homeRoot);
  if (cleanup) cleanup();
  revealControllers.delete(homeRoot);
  homeRoot.querySelectorAll(".reveal").forEach((element) => element.classList.add("is-visible"));
};

const initReveals = (homeRoot) => {
  if (!homeRoot || revealControllers.has(homeRoot)) return;
  const elements = Array.from(homeRoot.querySelectorAll(".reveal"));
  if (!elements.length) return;

  if (reduceMotionMedia.matches || !("IntersectionObserver" in window)) {
    elements.forEach((element) => element.classList.add("is-visible"));
    revealControllers.set(homeRoot, () => {});
    return;
  }

  document.documentElement.classList.add("ph-motion-ready");
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        revealObserver.unobserve(entry.target);
      });
    },
    { threshold: 0.12, rootMargin: "0px 0px -6%" },
  );

  elements.forEach((element) => {
    element.classList.remove("is-visible");
    revealObserver.observe(element);
  });
  revealControllers.set(homeRoot, () => revealObserver.disconnect());
};

const storyControllers = new Map();

const resetDeliveryStory = (deliveryStory) => {
  if (!deliveryStory) return;

  const storySteps = Array.from(deliveryStory.querySelectorAll("[data-story-step]"));
  const storyScenes = Array.from(deliveryStory.querySelectorAll("[data-story-scene]"));
  const storyProgress = Array.from(deliveryStory.querySelectorAll(".story-progress span"));
  const storyStatusNumber = deliveryStory.querySelector(".story-status-number");
  const storyStatusLabel = deliveryStory.querySelector(".story-status-label");

  delete deliveryStory.dataset.storyEnhanced;
  deliveryStory.classList.remove("is-enhanced", "is-complete");
  storySteps.forEach((step, index) => {
    step.classList.toggle("is-active", index === 0);
    step.removeAttribute("aria-current");
  });
  storyScenes.forEach((scene, index) => scene.classList.toggle("is-active", index === 0));
  storyProgress.forEach((marker, index) => marker.classList.toggle("is-active", index === 0));
  if (storyStatusNumber) storyStatusNumber.textContent = "01";
  if (storyStatusLabel && storySteps[0]) {
    storyStatusLabel.textContent = storySteps[0].dataset.storyStatus || "";
  }
};

const destroyDeliveryStory = (deliveryStory) => {
  const cleanup = storyControllers.get(deliveryStory);
  if (cleanup) cleanup();
  storyControllers.delete(deliveryStory);
  resetDeliveryStory(deliveryStory);
};

const initDeliveryStory = (deliveryStory) => {
  if (!deliveryStory || storyControllers.has(deliveryStory)) return;

  const storySteps = Array.from(deliveryStory.querySelectorAll("[data-story-step]"));
  const storyScenes = Array.from(deliveryStory.querySelectorAll("[data-story-scene]"));
  if (!storySteps.length || !storyScenes.length) return;

  // Compact, short and reduced-motion viewports retain the complete static card sequence.
  if (!storyMotionMedia.matches || !("IntersectionObserver" in window)) return;

  const storyProgress = Array.from(deliveryStory.querySelectorAll(".story-progress span"));
  const storyStatusNumber = deliveryStory.querySelector(".story-status-number");
  const storyStatusLabel = deliveryStory.querySelector(".story-status-label");
  let activeStoryStep = -1;

  deliveryStory.dataset.storyEnhanced = "true";
  deliveryStory.classList.add("is-enhanced");

  const setStoryStep = (requestedIndex) => {
    const index = Math.max(0, Math.min(requestedIndex, storySteps.length - 1));
    if (index === activeStoryStep) return;
    activeStoryStep = index;

    const activeStep = storySteps[index];
    const sceneIndex = Number(activeStep.dataset.storySceneIndex);

    storySteps.forEach((step, stepIndex) => {
      const isActive = stepIndex === index;
      step.classList.toggle("is-active", isActive);
      if (isActive) step.setAttribute("aria-current", "step");
      else step.removeAttribute("aria-current");
    });
    storyScenes.forEach((scene) => {
      scene.classList.toggle(
        "is-active",
        Number.isFinite(sceneIndex) && Number(scene.dataset.storyScene) === sceneIndex,
      );
    });
    storyProgress.forEach((marker, markerIndex) => {
      marker.classList.toggle("is-active", markerIndex === index);
    });

    deliveryStory.classList.toggle("is-complete", index === storySteps.length - 1);

    if (storyStatusNumber) storyStatusNumber.textContent = String(index + 1).padStart(2, "0");
    if (storyStatusLabel) storyStatusLabel.textContent = activeStep.dataset.storyStatus || "";
  };

  setStoryStep(0);

  let storyVisible = true;
  let storyTicking = false;
  let animationFrame = 0;

  const updateStoryFromScroll = () => {
    const viewportTarget = window.innerHeight * 0.52;
    const nearestStep = storySteps.reduce(
      (nearest, step, index) => {
        const bounds = step.getBoundingClientRect();
        const distance = Math.abs(bounds.top + bounds.height / 2 - viewportTarget);
        return distance < nearest.distance ? { index, distance } : nearest;
      },
      { index: 0, distance: Number.POSITIVE_INFINITY },
    );
    setStoryStep(nearestStep.index);
    storyTicking = false;
    animationFrame = 0;
  };

  const requestStoryUpdate = () => {
    if (!storyVisible || storyTicking) return;
    storyTicking = true;
    animationFrame = window.requestAnimationFrame(updateStoryFromScroll);
  };

  window.addEventListener("scroll", requestStoryUpdate, { passive: true });
  window.addEventListener("resize", requestStoryUpdate);

  const storyObserver = new IntersectionObserver(
    ([entry]) => {
      storyVisible = entry.isIntersecting;
      if (storyVisible) requestStoryUpdate();
    },
    { rootMargin: "20% 0px" },
  );
  storyObserver.observe(deliveryStory);

  const cleanup = () => {
    window.removeEventListener("scroll", requestStoryUpdate);
    window.removeEventListener("resize", requestStoryUpdate);
    storyObserver.disconnect();
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
  };

  storyControllers.set(deliveryStory, cleanup);
  requestStoryUpdate();
};

const syncDeliveryStories = (scope = document) => {
  scope.querySelectorAll?.("[data-delivery-story]").forEach((deliveryStory) => {
    if (storyMotionMedia.matches) initDeliveryStory(deliveryStory);
    else destroyDeliveryStory(deliveryStory);
  });
};

const carouselControllers = new Map();

const destroyCarousel = (carousel) => {
  const cleanup = carouselControllers.get(carousel);
  if (cleanup) cleanup();
  carouselControllers.delete(carousel);
};

const initCarousel = (carousel) => {
  if (!carousel || carouselControllers.has(carousel)) return;

  const appTrack = carousel.querySelector(".app-track");
  const slides = Array.from(carousel.querySelectorAll(".app-slide"));
  if (!appTrack || !slides.length) return;

  const previousButton = carousel.querySelector(".carousel-previous");
  const nextButton = carousel.querySelector(".carousel-next");
  const indicators = Array.from(carousel.querySelectorAll(".carousel-indicators button"));
  const carouselStatus = carousel.querySelector(".carousel-status");
  let currentSlide = 0;
  let scrollTimer = 0;

  const updateCarousel = (index) => {
    currentSlide = Math.max(0, Math.min(index, slides.length - 1));
    indicators.forEach((indicator, indicatorIndex) => {
      indicator.setAttribute("aria-current", String(indicatorIndex === currentSlide));
    });
    if (previousButton) previousButton.disabled = currentSlide === 0;
    if (nextButton) nextButton.disabled = currentSlide === slides.length - 1;
    if (carouselStatus) {
      carouselStatus.textContent = `Screen ${currentSlide + 1} of ${slides.length}: ${slides[currentSlide].dataset.title}`;
    }
  };

  const goToSlide = (index) => {
    const target = slides[Math.max(0, Math.min(index, slides.length - 1))];
    if (!target) return;
    appTrack.scrollTo({
      left: target.offsetLeft - appTrack.offsetLeft,
      behavior: reduceMotionMedia.matches ? "auto" : "smooth",
    });
    updateCarousel(slides.indexOf(target));
  };

  const handlePrevious = () => goToSlide(currentSlide - 1);
  const handleNext = () => goToSlide(currentSlide + 1);
  const handleIndicator = (event) => goToSlide(Number(event.currentTarget.dataset.slide));
  const handleKeydown = (event) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    if (event.key === "ArrowLeft") goToSlide(currentSlide - 1);
    if (event.key === "ArrowRight") goToSlide(currentSlide + 1);
    if (event.key === "Home") goToSlide(0);
    if (event.key === "End") goToSlide(slides.length - 1);
  };
  const handleScroll = () => {
    window.clearTimeout(scrollTimer);
    scrollTimer = window.setTimeout(() => {
      const trackLeft = appTrack.getBoundingClientRect().left;
      const nearestIndex = slides.reduce(
        (nearest, slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
          return distance < nearest.distance ? { index, distance } : nearest;
        },
        { index: 0, distance: Number.POSITIVE_INFINITY },
      ).index;
      updateCarousel(nearestIndex);
    }, 90);
  };

  previousButton?.addEventListener("click", handlePrevious);
  nextButton?.addEventListener("click", handleNext);
  indicators.forEach((indicator) => indicator.addEventListener("click", handleIndicator));
  appTrack.addEventListener("keydown", handleKeydown);
  appTrack.addEventListener("scroll", handleScroll, { passive: true });

  carouselControllers.set(carousel, () => {
    previousButton?.removeEventListener("click", handlePrevious);
    nextButton?.removeEventListener("click", handleNext);
    indicators.forEach((indicator) => indicator.removeEventListener("click", handleIndicator));
    appTrack.removeEventListener("keydown", handleKeydown);
    appTrack.removeEventListener("scroll", handleScroll);
    window.clearTimeout(scrollTimer);
  });
  updateCarousel(0);
};

const mobileActionControllers = new Map();

const destroyMobileAction = (homeRoot) => {
  const cleanup = mobileActionControllers.get(homeRoot);
  if (cleanup) cleanup();
  mobileActionControllers.delete(homeRoot);
};

const initMobileAction = (homeRoot) => {
  if (!homeRoot || mobileActionControllers.has(homeRoot)) return;

  const mobileAction = homeRoot.querySelector(".mobile-action");
  const hero = homeRoot.querySelector(".hero-home");
  const finalCta = homeRoot.querySelector(".final-cta");
  if (!mobileAction || !hero || !finalCta || !("IntersectionObserver" in window)) return;

  const actionLink = mobileAction.querySelector("a");
  let heroVisible = true;
  let finalVisible = false;

  const updateMobileAction = () => {
    const visible = !heroVisible && !finalVisible;
    mobileAction.classList.toggle("is-visible", visible);
    if (actionLink) actionLink.tabIndex = visible ? 0 : -1;
  };

  if (actionLink) actionLink.tabIndex = -1;
  const heroObserver = new IntersectionObserver(
    ([entry]) => {
      heroVisible = entry.isIntersecting;
      updateMobileAction();
    },
    { threshold: 0.08 },
  );
  const finalObserver = new IntersectionObserver(
    ([entry]) => {
      finalVisible = entry.isIntersecting;
      updateMobileAction();
    },
    { threshold: 0.12 },
  );

  heroObserver.observe(hero);
  finalObserver.observe(finalCta);
  mobileActionControllers.set(homeRoot, () => {
    heroObserver.disconnect();
    finalObserver.disconnect();
    mobileAction.classList.remove("is-visible");
    if (actionLink) actionLink.tabIndex = -1;
  });
};

const initializeHomepage = (scope = document) => {
  homeRootsWithin(scope).forEach((homeRoot) => {
    initReveals(homeRoot);
    syncDeliveryStories(homeRoot);
    homeRoot.querySelectorAll(".app-carousel").forEach(initCarousel);
    initMobileAction(homeRoot);
  });
};

const destroyHomepage = (scope) => {
  homeRootsWithin(scope).forEach((homeRoot) => {
    destroyReveals(homeRoot);
    homeRoot.querySelectorAll("[data-delivery-story]").forEach(destroyDeliveryStory);
    homeRoot.querySelectorAll(".app-carousel").forEach(destroyCarousel);
    destroyMobileAction(homeRoot);
  });
};

initializeHomepage();

addMediaChangeListener(storyMotionMedia, () => syncDeliveryStories());
addMediaChangeListener(reduceMotionMedia, () => {
  homeRootsWithin(document).forEach((homeRoot) => {
    destroyReveals(homeRoot);
    initReveals(homeRoot);
  });
});

// Shopify redraws sections without reloading the page while the theme is being edited.
document.addEventListener("shopify:section:load", (event) => initializeHomepage(event.target));
document.addEventListener("shopify:section:unload", (event) => destroyHomepage(event.target));
